import { Component, ReactNode } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { AppError } from '../utils/errorHandler'
import { isChunkLoadError, reloadForStaleShellOnce } from '../utils/chunkLoadError'
import logger from '../services/logger'

interface Props extends WithTranslation {
  children: ReactNode
  fallback?: (error: AppError, reset: () => void) => ReactNode
  /**
   * Test seam for the stale-shell auto-reload. Defaults to the guarded
   * reload (once per tab session); the boundary cannot be exercised with a
   * real reload in jsdom.
   */
  reloadStaleShell?: () => boolean
}

interface State {
  hasError: boolean
  error: AppError | null
  /** The error was a failed lazy-chunk import — a stale app shell. */
  staleShell: boolean
  /** A recovery reload has been initiated; keep the UI quiet while it runs. */
  shellReloading: boolean
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, staleShell: false, shellReloading: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error: {
        message: error.message || 'Something went wrong',
        details: error.stack,
        type: 'unknown',
        canRetry: true,
      },
      staleShell: isChunkLoadError(error),
      shellReloading: false,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // A failed dynamic import means this tab runs a shell older than the
    // server: its chunk hashes no longer exist and only a full reload (fresh
    // shell) recovers. Reload once automatically; on the second failure show
    // the manual reload card instead.
    if (this.state.staleShell) {
      const initiated = (this.props.reloadStaleShell ?? reloadForStaleShellOnce)()
      if (initiated) this.setState({ shellReloading: true })
      return
    }

    // Log error to console in development
    if (import.meta.env.DEV) {
      logger.error('Error caught by boundary', error, { errorInfo })
    }

    // In production, you could send this to an error tracking service
    // Example: sendErrorToService(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    // Recovery reload in flight: keep the screen quiet instead of flashing
    // the error card for the fraction of a second the reload takes.
    if (this.state.shellReloading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <p className="text-slate-600">{this.props.t('errors.staleShell.reloading')}</p>
        </div>
      )
    }

    if (this.state.hasError && this.state.error) {
      const { t } = this.props

      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset)
      }

      // Stale shell after the automatic reload already ran once: offer the
      // only action that can recover (a full document reload). "Try again"
      // would re-render the same rejected lazy component for nothing.
      if (this.state.staleShell) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
              <div className="flex flex-col items-center text-center space-y-4">
                <svg
                  className="w-16 h-16 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">
                    {t('errors.staleShell.title')}
                  </h2>
                  <p className="text-slate-600 mb-4">{t('errors.staleShell.description')}</p>
                </div>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  {t('errors.staleShell.reload')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <svg
                className="w-16 h-16 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>

              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  {t('errors.unknown.title')}
                </h2>
                <p className="text-slate-600 mb-4">
                  {this.state.error.message}
                </p>
              </div>

              <div className="flex gap-3 w-full">
                <button
                  onClick={this.handleReset}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  {t('errors.tryAgain')}
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex-1 px-4 py-2 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  {t('errors.goHome')}
                </button>
              </div>

              {import.meta.env.DEV && this.state.error.details && (
                <details className="mt-4 w-full text-start">
                  <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-900">
                    Error details (dev only)
                  </summary>
                  <pre className="mt-2 p-3 bg-slate-100 rounded-sm text-xs overflow-auto max-h-40">
                    {this.state.error.details}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Export with HOC wrapper for i18n
const ErrorBoundaryWithTranslation = withTranslation()(ErrorBoundary)
ErrorBoundaryWithTranslation.displayName = 'ErrorBoundaryWithTranslation'
export default ErrorBoundaryWithTranslation
